/**
 * Custom element for a single offer card inside blocks/flowstar-bundle.liquid.
 *
 * Matches the shopper's option selections (dropdowns or pills) to a real variant
 * id, then updates the hidden variant input, the Add button's disabled state, and
 * the card's two prices plus its savings badge.
 *
 * Also drives the photo viewport: clicking the image advances to the next photo
 * and wraps back to the first after the last, the same endless click-through the
 * apparel page's Bundle & Save V2 block uses. It works identically on touch and
 * pointer devices because it depends on click alone, never hover.
 *
 * Deliberately a plain HTMLElement rather than a Horizon Component — same as
 * assets/offer-row.js — so it never becomes a ref boundary for the
 * product-form-component nested inside it. That component owns the actual
 * add-to-cart submission; this element only decides which variant it submits.
 *
 * Both prices arrive pre-formatted in the variants JSON, rendered server-side by
 * Liquid's money filter, so there is no currency formatting in here to drift out
 * of step with the shop's money format.
 */
class FlowstarBundleCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);

    // The module script is deferred, so children are normally parsed by now —
    // but upgrade order is not guaranteed, and #recompute reads a <script> tag
    // that lives at the end of the card. Retry once on the next task if the
    // card was not fully populated yet.
    if (!this.#readVariants().length) {
      setTimeout(() => this.#recompute(), 0);
    } else {
      this.#recompute();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const { target } = event;
    // Element rather than HTMLElement: a pill could contain an inline <svg>,
    // whose events report an SVGElement target.
    if (!(target instanceof Element)) return;

    const viewport = target.closest('[data-fsb-image-viewport]');
    if (viewport && this.contains(viewport)) {
      this.#advanceImage(viewport);
      return;
    }

    const pill = target.closest('.fsb-card__pill');
    if (!pill || !this.contains(pill)) return;

    const group = pill.closest('[data-option-index]');
    if (!group) return;

    group.querySelectorAll('.fsb-card__pill').forEach((el) => {
      el.classList.remove('is-selected');
      el.setAttribute('aria-pressed', 'false');
    });
    pill.classList.add('is-selected');
    pill.setAttribute('aria-pressed', 'true');

    this.#recompute();
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const { target } = event;
    if (!(target instanceof HTMLSelectElement)) return;
    if (!target.matches('[data-fsb-select]')) return;
    this.#recompute();
  };

  /**
   * @returns {Array<{id: number, available: boolean, options: string[], price: string, bundlePrice: string, discounted: boolean}>}
   */
  #readVariants() {
    const script = this.querySelector('[data-fsb-variants]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent ?? '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  /** @returns {Element[]} */
  #readGroups() {
    return Array.from(this.querySelectorAll('[data-option-index]')).sort(
      (a, b) => Number(a.getAttribute('data-option-index')) - Number(b.getAttribute('data-option-index'))
    );
  }

  /**
   * One selected value per option group, in option order.
   * @param {Element[]} groups
   * @returns {(string | null)[]}
   */
  #readSelectedValues(groups) {
    return groups.map((group) => {
      const select = group.querySelector('[data-fsb-select]');
      if (select instanceof HTMLSelectElement) return select.value;

      const selectedPill = group.querySelector('.fsb-card__pill.is-selected');
      if (selectedPill) return selectedPill.getAttribute('data-value');

      // No pill marked selected (possible if Liquid rendered no default) —
      // adopt the first one so the card can never sit in a state where the
      // Add button has no variant to submit.
      const firstPill = group.querySelector('.fsb-card__pill');
      if (firstPill) {
        firstPill.classList.add('is-selected');
        firstPill.setAttribute('aria-pressed', 'true');
        return firstPill.getAttribute('data-value');
      }

      return null;
    });
  }

  #recompute() {
    const variants = this.#readVariants();
    if (variants.length === 0) return;

    const groups = this.#readGroups();

    /** @type {{id: number, available: boolean, options: string[], price: string, bundlePrice: string, discounted: boolean} | undefined} */
    let matched;

    if (groups.length === 0) {
      matched = variants.find((variant) => variant.available) ?? variants[0];
    } else {
      const selected = this.#readSelectedValues(groups);
      if (selected.some((value) => value == null)) return;

      matched = variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
      this.#markUnavailable(groups, variants, selected);
    }

    const variantInput = this.querySelector('[data-fsb-variant-input]');
    if (variantInput instanceof HTMLInputElement && matched) {
      variantInput.value = String(matched.id);
    }

    const addButton = this.querySelector('[ref="addToCartButton"]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = matched ? !matched.available : true;
    }

    if (!matched) return;

    const priceEl = this.querySelector('[data-fsb-bundle-price]');
    if (priceEl) priceEl.textContent = matched.bundlePrice;

    const compareEl = this.querySelector('[data-fsb-compare-price]');
    if (compareEl) {
      compareEl.textContent = matched.price;
      compareEl.toggleAttribute('hidden', !matched.discounted);
    }

    const badgeEl = this.querySelector('[data-fsb-badge]');
    if (badgeEl) {
      badgeEl.toggleAttribute('hidden', !matched.discounted);
    }
  }

  /**
   * Advances the photo viewport by one, wrapping around at the end, and syncs
   * the dot indicators to match.
   *
   * @param {Element} viewport
   */
  #advanceImage(viewport) {
    const images = Array.from(viewport.querySelectorAll('.fsb-card__image'));
    if (images.length < 2) return;

    // If nothing is marked active (unexpected, but cheap to survive) treat it as
    // sitting on the first image so the click still moves things along.
    const currentIndex = images.findIndex((image) => image.classList.contains('is-active'));
    const nextIndex = (currentIndex + 1) % images.length;

    images.forEach((image, index) => {
      image.classList.toggle('is-active', index === nextIndex);
    });

    const dots = this.querySelectorAll('[data-fsb-dots] .fsb-card__dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === nextIndex);
    });
  }

  /**
   * Strikes through any pill that, given the shopper's other selections, leads
   * only to sold-out variants. Dropdowns are left alone: striking through an
   * <option> is not reliably styleable across browsers, and the Add button
   * already goes disabled when the chosen combination is unavailable.
   *
   * @param {Element[]} groups
   * @param {Array<{available: boolean, options: string[]}>} variants
   * @param {(string | null)[]} selected
   */
  #markUnavailable(groups, variants, selected) {
    groups.forEach((group, groupIndex) => {
      group.querySelectorAll('.fsb-card__pill').forEach((pill) => {
        const candidate = selected.slice();
        candidate[groupIndex] = pill.getAttribute('data-value');

        const reachable = variants.some(
          (variant) => variant.available && variant.options.every((option, index) => option === candidate[index])
        );

        pill.classList.toggle('is-unavailable', !reachable);
      });
    });
  }
}

if (!customElements.get('flowstar-bundle-card')) {
  customElements.define('flowstar-bundle-card', FlowstarBundleCard);
}
